import { useState, useEffect, useMemo, useCallback } from 'react';
import { appointmentsAPI, clientsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, Clock, MapPin, User,
  ExternalLink, CheckCircle2, XCircle, Loader2, Copy, Phone,
} from 'lucide-react';

const TYPE_COLORS = {
  consultation: { bg: 'rgba(59,130,246,0.10)', text: '#2563EB', border: 'rgba(59,130,246,0.3)' },
  measurement: { bg: 'rgba(139,92,246,0.10)', text: '#7C3AED', border: 'rgba(139,92,246,0.3)' },
  trial: { bg: 'rgba(197,165,90,0.12)', text: '#92700C', border: 'rgba(197,165,90,0.4)' },
  delivery: { bg: 'rgba(16,185,129,0.10)', text: '#059669', border: 'rgba(16,185,129,0.3)' },
  alteration_check: { bg: 'rgba(245,158,11,0.10)', text: '#D97706', border: 'rgba(245,158,11,0.3)' },
  other: { bg: 'rgba(107,114,128,0.10)', text: '#6B7280', border: 'rgba(107,114,128,0.3)' },
};

const TYPE_LABELS = {
  consultation: 'Consultation', measurement: 'Measurement', trial: 'Trial',
  delivery: 'Delivery', alteration_check: 'Alteration', other: 'Other',
};

const LOCATION_LABELS = {
  colaba_store: 'Colaba Store', client_address: 'Client Address',
  popup_venue: 'Pop-Up Venue', video_call: 'Video Call',
};

const STATUS_CFG = {
  scheduled: { bg: 'rgba(59,130,246,0.08)', text: '#2563EB' },
  confirmed: { bg: 'rgba(16,185,129,0.08)', text: '#059669' },
  completed: { bg: 'rgba(107,114,128,0.08)', text: '#6B7280' },
  cancelled: { bg: 'rgba(239,68,68,0.08)', text: '#DC2626' },
  no_show: { bg: 'rgba(245,158,11,0.08)', text: '#D97706' },
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function formatDate(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }

export default function AppointmentsPage() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedApt, setSelectedApt] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingDate, setBookingDate] = useState(null);

  const dateRange = useMemo(() => {
    const y = currentDate.getFullYear(), m = currentDate.getMonth();
    if (view === 'month') {
      const first = new Date(y, m, 1);
      const last = new Date(y, m + 1, 0);
      const start = new Date(first); start.setDate(start.getDate() - first.getDay());
      const end = new Date(last); end.setDate(end.getDate() + (6 - last.getDay()));
      return { start: formatDate(start), end: formatDate(end) };
    }
    if (view === 'week') {
      const d = new Date(currentDate);
      d.setDate(d.getDate() - d.getDay());
      const end = new Date(d); end.setDate(end.getDate() + 6);
      return { start: formatDate(d), end: formatDate(end) };
    }
    return { start: formatDate(currentDate), end: formatDate(currentDate) };
  }, [currentDate, view]);

  const fetchAppointments = useCallback(async () => {
    try {
      const res = await appointmentsAPI.list({ start_date: dateRange.start, end_date: dateRange.end });
      setAppointments(res.data || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [dateRange]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  const navigate_date = (dir) => {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else if (view === 'week') d.setDate(d.getDate() + dir * 7);
    else d.setDate(d.getDate() + dir);
    setCurrentDate(d);
  };

  const aptsByDate = useMemo(() => {
    const map = {};
    appointments.forEach(a => { (map[a.date] = map[a.date] || []).push(a); });
    return map;
  }, [appointments]);

  const today = formatDate(new Date());

  // ---- Month View ----
  const monthDays = useMemo(() => {
    const y = currentDate.getFullYear(), m = currentDate.getMonth();
    const first = new Date(y, m, 1);
    const start = new Date(first); start.setDate(start.getDate() - first.getDay());
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start); d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  }, [currentDate]);

  // ---- Week View ----
  const weekDays = useMemo(() => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - d.getDay());
    return Array.from({ length: 7 }, (_, i) => { const dd = new Date(d); dd.setDate(dd.getDate() + i); return dd; });
  }, [currentDate]);

  const handleBookingCreated = () => {
    setBookingOpen(false);
    setBookingDate(null);
    fetchAppointments();
  };

  return (
    <div className="space-y-4" data-testid="appointments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--brand-navy)' }}>Appointments</h1>
          <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>{MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border rounded-lg overflow-hidden" style={{ borderColor: 'rgba(27,42,74,0.12)' }}>
            {['month', 'week', 'day'].map(v => (
              <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${view === v ? 'text-white' : 'hover:bg-gray-50'}`}
                style={view === v ? { backgroundColor: 'var(--brand-navy)' } : { color: 'var(--ink-muted)' }}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigate_date(-1)}><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => setCurrentDate(new Date())}>Today</Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigate_date(1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <Button className="gap-1.5 text-xs h-9 rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}
            onClick={() => { setBookingDate(formatDate(new Date())); setBookingOpen(true); }} data-testid="new-appointment-btn">
            <Plus className="w-3.5 h-3.5" /> New Appointment
          </Button>
        </div>
      </div>

      {loading ? <Skeleton className="h-96 w-full rounded-xl" /> : (
        <>
          {/* Month View */}
          {view === 'month' && (
            <Card className="rounded-xl border border-border/80 bg-white shadow-sm overflow-hidden">
              <div className="grid grid-cols-7">
                {DAYS.map(d => (
                  <div key={d} className="text-center py-2 text-[10px] font-semibold border-b" style={{ color: 'var(--ink-muted)', borderColor: 'rgba(27,42,74,0.06)' }}>{d}</div>
                ))}
                {monthDays.map((day, i) => {
                  const ds = formatDate(day);
                  const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                  const isToday = ds === today;
                  const dayApts = aptsByDate[ds] || [];
                  return (
                    <div key={i} className="min-h-[90px] p-1 border-b border-r cursor-pointer hover:bg-gray-50/50 transition-colors"
                      style={{ borderColor: 'rgba(27,42,74,0.04)', opacity: isCurrentMonth ? 1 : 0.35 }}
                      onClick={() => { setBookingDate(ds); setBookingOpen(true); }}
                      data-testid={`cal-day-${ds}`}>
                      <div className={`text-[11px] font-medium w-6 h-6 flex items-center justify-center rounded-full mb-0.5 ${isToday ? 'text-white' : ''}`}
                        style={isToday ? { backgroundColor: 'var(--brand-navy)' } : { color: isCurrentMonth ? 'var(--navy-ink)' : 'var(--ink-muted)' }}>
                        {day.getDate()}
                      </div>
                      {dayApts.slice(0, 3).map(a => {
                        const tc = TYPE_COLORS[a.appointment_type] || TYPE_COLORS.other;
                        return (
                          <div key={a.id} className="text-[9px] px-1 py-0.5 rounded mb-0.5 truncate cursor-pointer"
                            style={{ backgroundColor: tc.bg, color: tc.text }}
                            onClick={(e) => { e.stopPropagation(); setSelectedApt(a); }}>
                            {a.start_time} {a.client_name?.split(' ')[0]}
                          </div>
                        );
                      })}
                      {dayApts.length > 3 && <p className="text-[8px] px-1" style={{ color: 'var(--ink-muted)' }}>+{dayApts.length - 3} more</p>}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Week View */}
          {view === 'week' && (
            <Card className="rounded-xl border border-border/80 bg-white shadow-sm overflow-hidden">
              <div className="grid grid-cols-7 divide-x" style={{ borderColor: 'rgba(27,42,74,0.06)' }}>
                {weekDays.map(day => {
                  const ds = formatDate(day);
                  const isToday = ds === today;
                  const dayApts = aptsByDate[ds] || [];
                  return (
                    <div key={ds} className="min-h-[400px]">
                      <div className={`text-center py-2 border-b ${isToday ? 'bg-[color:var(--brand-navy)]' : ''}`} style={{ borderColor: 'rgba(27,42,74,0.06)' }}>
                        <p className="text-[10px] font-medium" style={{ color: isToday ? 'rgba(255,255,255,0.7)' : 'var(--ink-muted)' }}>{DAYS[day.getDay()]}</p>
                        <p className="text-lg font-bold" style={{ color: isToday ? '#fff' : 'var(--brand-navy)' }}>{day.getDate()}</p>
                      </div>
                      <div className="p-1 space-y-1">
                        {dayApts.map(a => {
                          const tc = TYPE_COLORS[a.appointment_type] || TYPE_COLORS.other;
                          return (
                            <div key={a.id} className="p-1.5 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow"
                              style={{ borderColor: tc.border, backgroundColor: tc.bg }}
                              onClick={() => setSelectedApt(a)}>
                              <p className="text-[10px] font-semibold" style={{ color: tc.text }}>{a.start_time} - {a.end_time}</p>
                              <p className="text-[10px] truncate" style={{ color: 'var(--navy-ink)' }}>{a.client_name}</p>
                              <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>{TYPE_LABELS[a.appointment_type]}</p>
                            </div>
                          );
                        })}
                        {dayApts.length === 0 && <p className="text-[9px] text-center py-6" style={{ color: 'var(--ink-muted)' }}>No appts</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Day View */}
          {view === 'day' && (
            <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>
                  {currentDate.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(aptsByDate[formatDate(currentDate)] || []).length === 0 ? (
                  <div className="py-12 text-center">
                    <CalendarDays className="w-10 h-10 mx-auto mb-2" style={{ color: 'rgba(27,42,74,0.12)' }} />
                    <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No appointments</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(aptsByDate[formatDate(currentDate)] || []).map(a => {
                      const tc = TYPE_COLORS[a.appointment_type] || TYPE_COLORS.other;
                      const sc = STATUS_CFG[a.status] || STATUS_CFG.scheduled;
                      return (
                        <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow"
                          style={{ borderLeft: `3px solid ${tc.text}`, borderColor: 'rgba(27,42,74,0.08)' }}
                          onClick={() => setSelectedApt(a)} data-testid={`day-apt-${a.id}`}>
                          <div className="flex-shrink-0 text-center w-16">
                            <p className="text-sm font-bold" style={{ color: 'var(--brand-navy)' }}>{a.start_time}</p>
                            <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{a.end_time}</p>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate" style={{ color: 'var(--navy-ink)' }}>{a.client_name}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <Badge variant="outline" className="text-[9px] px-1 py-0" style={{ backgroundColor: tc.bg, color: tc.text }}>{TYPE_LABELS[a.appointment_type]}</Badge>
                              <Badge variant="outline" className="text-[9px] px-1 py-0" style={{ backgroundColor: sc.bg, color: sc.text }}>{a.status}</Badge>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                            <MapPin className="w-3 h-3" /> {LOCATION_LABELS[a.location] || a.location}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Appointment Detail Dialog */}
      <AppointmentDetailDialog appointment={selectedApt} onClose={() => setSelectedApt(null)} onUpdate={fetchAppointments} navigate={navigate} />

      {/* Booking Dialog */}
      <BookAppointmentDialog open={bookingOpen} onOpenChange={setBookingOpen} initialDate={bookingDate} onCreated={handleBookingCreated} />
    </div>
  );
}

/* ========== Appointment Detail Dialog ========== */
function AppointmentDetailDialog({ appointment, onClose, onUpdate, navigate }) {
  const [updating, setUpdating] = useState(false);
  const [fullAppointment, setFullAppointment] = useState(null);

  useEffect(() => {
    if (appointment?.id) {
      appointmentsAPI.get(appointment.id).then(res => setFullAppointment(res.data)).catch(() => {});
    }
  }, [appointment?.id]);

  if (!appointment) return null;

  const apt = fullAppointment || appointment;
  const tc = TYPE_COLORS[apt.appointment_type] || TYPE_COLORS.other;
  const sc = STATUS_CFG[apt.status] || STATUS_CFG.scheduled;

  const handleStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await appointmentsAPI.update(apt.id, { status: newStatus });
      toast.success(`Appointment ${newStatus}`);
      onClose(); onUpdate();
    } catch { toast.error('Failed'); }
    setUpdating(false);
  };

  const gcalUrl = apt.gcal_url || '#';

  return (
    <Dialog open={true} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-md" data-testid="appointment-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <CalendarDays className="w-4 h-4" /> Appointment Details
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Badge className="text-[10px]" style={{ backgroundColor: tc.bg, color: tc.text }}>{TYPE_LABELS[apt.appointment_type]}</Badge>
            <Badge className="text-[10px]" style={{ backgroundColor: sc.bg, color: sc.text }}>{apt.status}</Badge>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2"><User className="w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} />
              <div>
                <p className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>{apt.client_name}</p>
                {apt.client_phone && <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{apt.client_phone}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} />
              <div>
                <p className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>{apt.date}</p>
                <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{apt.start_time} - {apt.end_time}</p>
              </div>
            </div>
            <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} />
              <p className="text-xs" style={{ color: 'var(--navy-ink)' }}>{LOCATION_LABELS[apt.location] || apt.location}</p>
            </div>
            {apt.order_number && (
              <button className="flex items-center gap-2 text-left" onClick={() => { onClose(); navigate(`/app/orders/${apt.order_id}`); }}>
                <p className="text-xs underline" style={{ color: 'var(--brand-navy)' }}>{apt.order_number}</p>
              </button>
            )}
          </div>
          {apt.notes && <p className="text-xs p-2 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.03)', color: 'var(--navy-ink)' }}>{apt.notes}</p>}
          <a href={gcalUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border hover:bg-gray-50 transition-colors"
            style={{ borderColor: 'rgba(27,42,74,0.12)', color: 'var(--brand-navy)' }} data-testid="gcal-link">
            <ExternalLink className="w-3.5 h-3.5" /> Add to Google Calendar
          </a>
        </div>
        <DialogFooter>
          {apt.status === 'scheduled' && (
            <>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => handleStatus('confirmed')} disabled={updating} data-testid="confirm-apt-btn">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Confirm
              </Button>
              <Button variant="outline" size="sm" className="text-xs text-red-600 border-red-200" onClick={() => handleStatus('cancelled')} disabled={updating}>
                <XCircle className="w-3 h-3 mr-1" /> Cancel
              </Button>
            </>
          )}
          {apt.status === 'confirmed' && (
            <Button size="sm" className="text-xs gap-1" style={{ backgroundColor: 'var(--brand-navy)' }}
              onClick={() => handleStatus('completed')} disabled={updating}>
              <CheckCircle2 className="w-3 h-3" /> Mark Complete
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ========== Book Appointment Dialog ========== */
export function BookAppointmentDialog({ open, onOpenChange, initialDate, onCreated, prefillType, prefillOrderId, prefillClientId }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(prefillClientId || '');
  const [orderId, setOrderId] = useState(prefillOrderId || '');
  const [type, setType] = useState(prefillType || 'consultation');
  const [date, setDate] = useState(initialDate || '');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [location, setLocation] = useState('colaba_store');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState('');

  useEffect(() => {
    if (open) {
      setClientId(prefillClientId || '');
      setOrderId(prefillOrderId || '');
      setType(prefillType || 'consultation');
      setDate(initialDate || formatDate(new Date()));
      setSelectedSlot('');
      setNotes('');
      clientsAPI.list().then(res => setClients(res.data?.clients || [])).catch(() => {});
    }
  }, [open, prefillClientId, prefillOrderId, prefillType, initialDate]);

  useEffect(() => {
    if (date && open) {
      setSlotsLoading(true);
      appointmentsAPI.slots(date).then(res => { setSlots(res.data || []); setSlotsLoading(false); }).catch(() => setSlotsLoading(false));
    }
  }, [date, open]);

  const filteredClients = useMemo(() => {
    if (!clientSearch) return clients.slice(0, 10);
    const q = clientSearch.toLowerCase();
    return clients.filter(c => `${c.first_name} ${c.last_name} ${c.phone}`.toLowerCase().includes(q)).slice(0, 10);
  }, [clients, clientSearch]);

  const handleSave = async () => {
    if (!clientId || !date || !selectedSlot) { toast.error('Select client, date, and time'); return; }
    setSaving(true);
    try {
      await appointmentsAPI.create({
        client_id: clientId, order_id: orderId || null,
        appointment_type: type, date, start_time: selectedSlot,
        location, notes,
      });
      toast.success('Appointment booked');
      if (onCreated) onCreated();
    } catch (e) { toast.error('Failed to book'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="book-appointment-dialog">
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--brand-navy)' }}>Book Appointment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Client */}
          {!prefillClientId && (
            <div>
              <Label className="text-xs" style={{ color: 'var(--ink-muted)' }}>Client</Label>
              <Input placeholder="Search client..." value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                className="h-8 mt-1 text-xs" data-testid="client-search-input" />
              {clientSearch && (
                <div className="mt-1 border rounded-lg max-h-32 overflow-y-auto" style={{ borderColor: 'rgba(27,42,74,0.1)' }}>
                  {filteredClients.map(c => (
                    <button key={c.id} className={`w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50 ${clientId === c.id ? 'bg-blue-50' : ''}`}
                      onClick={() => { setClientId(c.id); setClientSearch(`${c.first_name} ${c.last_name}`); }}>
                      {c.first_name} {c.last_name} <span style={{ color: 'var(--ink-muted)' }}>· {c.phone}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Type + Location */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs" style={{ color: 'var(--ink-muted)' }}>Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="h-8 mt-1 text-xs" data-testid="type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs" style={{ color: 'var(--ink-muted)' }}>Location</Label>
              <Select value={location} onValueChange={setLocation}>
                <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(LOCATION_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Date */}
          <div>
            <Label className="text-xs" style={{ color: 'var(--ink-muted)' }}>Date</Label>
            <Input type="date" value={date} onChange={e => { setDate(e.target.value); setSelectedSlot(''); }}
              className="h-8 mt-1 text-xs" data-testid="date-input" />
          </div>
          {/* Time Slots */}
          <div>
            <Label className="text-xs" style={{ color: 'var(--ink-muted)' }}>Available Time Slots</Label>
            {slotsLoading ? <Skeleton className="h-16 mt-1" /> : (
              <div className="grid grid-cols-4 gap-1.5 mt-1">
                {slots.filter(s => s.available > 0).map(s => (
                  <button key={s.start_time} onClick={() => setSelectedSlot(s.start_time)}
                    className={`px-2 py-1.5 text-xs rounded-lg border transition-colors ${selectedSlot === s.start_time ? 'text-white border-transparent' : 'hover:bg-gray-50'}`}
                    style={selectedSlot === s.start_time ? { backgroundColor: 'var(--brand-navy)' } : { borderColor: 'rgba(27,42,74,0.12)', color: 'var(--navy-ink)' }}
                    data-testid={`slot-${s.start_time}`}>
                    {s.start_time}
                  </button>
                ))}
                {slots.filter(s => s.available > 0).length === 0 && !slotsLoading && (
                  <p className="col-span-4 text-xs text-center py-3" style={{ color: 'var(--ink-muted)' }}>No available slots</p>
                )}
              </div>
            )}
          </div>
          {/* Notes */}
          <div>
            <Label className="text-xs" style={{ color: 'var(--ink-muted)' }}>Notes (optional)</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} className="mt-1 text-xs h-16" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !clientId || !selectedSlot}
            className="gap-1" style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="save-appointment-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CalendarDays className="w-3 h-3" />}
            {saving ? 'Booking...' : 'Book Appointment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
